import { BaseSDK, initSDK } from "@overmap-ai/core"
import type { Store } from "@reduxjs/toolkit"

import { appStore, type RootState } from "../store"
import {
	AgentService,
	AssetAttachmentService,
	AssetCommentAttachmentService,
	AssetCommentService,
	AssetService,
	AssetTypeAttachmentService,
	AssetTypeFieldsAttachmentService,
	AssetTypeFieldsService,
	AssetTypeFieldValuesAttachmentService,
	AssetTypeFieldValuesService,
	AssetTypeIdentifierService,
	AssetTypeIdentifierValueService,
	AssetTypeService,
	AssetTypeStatusService,
	DocumentAttachmentService,
	DocumentService,
	EmailDomainsService,
	EmailVerificationService,
	FileService,
	FormIdentifierService,
	FormIdentifierValueService,
	FormRevisionAttachmentService,
	FormRevisionService,
	FormService,
	FormSubmissionCommentAttachmentService,
	FormSubmissionCommentService,
	FormSubmissionFieldValuesAttachmentService,
	FormSubmissionFieldValuesService,
	FormSubmissionService,
	FormSubscriberService,
	GeoImageService,
	IssueAssociationService,
	IssueAttachmentService,
	IssueCommentAttachmentService,
	IssueCommentService,
	IssueService,
	IssueTypeAttachmentService,
	IssueTypeFieldsAttachmentService,
	IssueTypeFieldsService,
	IssueTypeFieldValuesAttachmentService,
	IssueTypeFieldValuesService,
	IssueTypeIdentifierService,
	IssueTypeIdentifierValueService,
	IssueTypePriorityService,
	IssueTypeService,
	IssueTypeStatusService,
	IssueUpdateService,
	MainService,
	OrganizationAccessService,
	OrganizationService,
	ProcedureFieldValuesAttachmentService,
	ProcedureFieldValuesService,
	ProcedureInitiativeService,
	ProcedureService,
	ProcedureStepAssigneeService,
	ProcedureStepCommentService,
	ProcedureStepDefaultAssigneeService,
	ProcedureStepDefaultReviewerService,
	ProcedureStepEventService,
	ProcedureStepFieldsAttachmentService,
	ProcedureStepFieldsService,
	ProcedureStepFieldValuesAttachmentService,
	ProcedureStepFieldValuesReviewService,
	ProcedureStepFieldValuesService,
	ProcedureStepReviewerService,
	ProcedureStepService,
	ProcedureTypeAttachmentService,
	ProcedureTypeFieldsAttachmentService,
	ProcedureTypeFieldsService,
	ProcedureTypeService,
	ProjectAccessService,
	ProjectAttachmentService,
	ProjectCommentService,
	ProjectFileService,
	ProjectService,
	ReportService,
	SessionAuthService,
	TeamMembershipService,
	TeamService,
	UserService,
} from "./services"

export class AppSDK extends BaseSDK<RootState> {
	constructor(store: Store<RootState>) {
		super(store)
	}

	auth = new SessionAuthService(this)
	agents = new AgentService(this, this.auth)
	assetAttachments = new AssetAttachmentService(this, this.auth)
	assetCommentAttachments = new AssetCommentAttachmentService(this, this.auth)
	assetComments = new AssetCommentService(this, this.auth)
	assets = new AssetService(this, this.auth)
	assetTypeAttachments = new AssetTypeAttachmentService(this, this.auth)
	assetTypes = new AssetTypeService(this, this.auth)
	assetTypeFields = new AssetTypeFieldsService(this, this.auth)
	assetTypeFieldValues = new AssetTypeFieldValuesService(this, this.auth)
	assetTypeFieldsAttachments = new AssetTypeFieldsAttachmentService(this, this.auth)
	assetTypeFieldValuesAttachments = new AssetTypeFieldValuesAttachmentService(this, this.auth)
	assetTypeIdentifiers = new AssetTypeIdentifierService(this, this.auth)
	assetTypeIdentifierValues = new AssetTypeIdentifierValueService(this, this.auth)
	assetTypeStatuses = new AssetTypeStatusService(this, this.auth)
	documentAttachments = new DocumentAttachmentService(this, this.auth)
	documents = new DocumentService(this, this.auth)
	emailDomains = new EmailDomainsService(this, this.auth)
	emailVerifications = new EmailVerificationService(this, this.auth)
	files = new FileService(this, this.auth)
	issueAttachments = new IssueAttachmentService(this, this.auth)
	issueCommentAttachments = new IssueCommentAttachmentService(this, this.auth)
	issueComments = new IssueCommentService(this, this.auth)
	issues = new IssueService(this, this.auth)
	issueTypes = new IssueTypeService(this, this.auth)
	issueTypeAttachments = new IssueTypeAttachmentService(this, this.auth)
	issueTypeFields = new IssueTypeFieldsService(this, this.auth)
	issueTypeFieldValues = new IssueTypeFieldValuesService(this, this.auth)
	issueTypeFieldsAttachments = new IssueTypeFieldsAttachmentService(this, this.auth)
	issueTypeFieldValuesAttachments = new IssueTypeFieldValuesAttachmentService(this, this.auth)
	issueTypeIdentifiers = new IssueTypeIdentifierService(this, this.auth)
	issueTypeIdentifierValues = new IssueTypeIdentifierValueService(this, this.auth)
	issueTypePriorities = new IssueTypePriorityService(this, this.auth)
	issueTypeStatuses = new IssueTypeStatusService(this, this.auth)
	issueUpdates = new IssueUpdateService(this, this.auth)
	main = new MainService(this, this.auth)
	organizationAccesses = new OrganizationAccessService(this, this.auth)
	organizations = new OrganizationService(this, this.auth)
	projectAccesses = new ProjectAccessService(this, this.auth)
	projectAttachments = new ProjectAttachmentService(this, this.auth)
	projectComments = new ProjectCommentService(this, this.auth)
	projectFiles = new ProjectFileService(this, this.auth)
	projects = new ProjectService(this, this.auth)
	reports = new ReportService(this, this.auth)
	procedureInitiatives = new ProcedureInitiativeService(this, this.auth)
	procedures = new ProcedureService(this, this.auth)
	procedureStepAssignees = new ProcedureStepAssigneeService(this, this.auth)
	procedureStepComments = new ProcedureStepCommentService(this, this.auth)
	procedureStepDefaultAssignees = new ProcedureStepDefaultAssigneeService(this, this.auth)
	procedureStepDefaultReviewers = new ProcedureStepDefaultReviewerService(this, this.auth)
	procedureStepEvents = new ProcedureStepEventService(this, this.auth)
	procedureStepFieldsAttachments = new ProcedureStepFieldsAttachmentService(this, this.auth)
	procedureStepFields = new ProcedureStepFieldsService(this, this.auth)
	procedureStepFieldValuesAttachments = new ProcedureStepFieldValuesAttachmentService(this, this.auth)
	procedureStepFieldValuesReviews = new ProcedureStepFieldValuesReviewService(this, this.auth)
	procedureStepFieldValues = new ProcedureStepFieldValuesService(this, this.auth)
	procedureStepReviewers = new ProcedureStepReviewerService(this, this.auth)
	procedureSteps = new ProcedureStepService(this, this.auth)
	procedureTypeAttachments = new ProcedureTypeAttachmentService(this, this.auth)
	procedureFieldValuesAttachments = new ProcedureFieldValuesAttachmentService(this, this.auth)
	procedureFieldValues = new ProcedureFieldValuesService(this, this.auth)
	procedureTypeFieldsAttachments = new ProcedureTypeFieldsAttachmentService(this, this.auth)
	procedureTypeFields = new ProcedureTypeFieldsService(this, this.auth)
	procedureTypes = new ProcedureTypeService(this, this.auth)
	teams = new TeamService(this, this.auth)
	teamMemberships = new TeamMembershipService(this, this.auth)
	forms = new FormService(this, this.auth)
	formIdentifiers = new FormIdentifierService(this, this.auth)
	formIdentifierValues = new FormIdentifierValueService(this, this.auth)
	formSubmissions = new FormSubmissionService(this, this.auth)
	formSubmissionCommentAttachments = new FormSubmissionCommentAttachmentService(this, this.auth)
	formSubmissionComments = new FormSubmissionCommentService(this, this.auth)
	formRevisions = new FormRevisionService(this, this.auth)
	formRevisionAttachments = new FormRevisionAttachmentService(this, this.auth)
	formSubmissionFieldValuesAttachments = new FormSubmissionFieldValuesAttachmentService(this, this.auth)
	users = new UserService(this, this.auth)
	geoImages = new GeoImageService(this, this.auth)
	issueAssociations = new IssueAssociationService(this, this.auth)
	formSubscribers = new FormSubscriberService(this, this.auth)
	formSubmissionFieldValues = new FormSubmissionFieldValuesService(this, this.auth)
}

export const sdk = initSDK(appStore, AppSDK)
